import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'

export type FibonacciCirclesDrawingState = {
    p1?: Point,
    p2?: Point
}

export type FibonacciCirclesDrawingSettings = {
    showTrendLine: boolean,
    lineWidth: number,
    labelColor: ColorValue,
}

class FibonacciCirclesDrawingTool extends DrawingBase<any, FibonacciCirclesDrawingSettings, FibonacciCirclesDrawingState> {

    center: Point = {
        id: 'center',
        x: 0 as Time,
        y: 0
    }
    scaledCoordinates = {
        p1x: 0,
        p2x: 0,
        p1y: 0,
        p2y: 0,
        centerX: 0,
        centerY: 0
    }
    sectionLevels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.618, 2.618, 3.618, 4.236]
    sections: Array<number[]> = []
    sectionColors: string[] = ['#787b86', '#f23645', '#ff9800', '#4caf50', '#089981', '#00bcd4', '#787b86', '#2962ff', '#f23645', '#9c27b0', '#e91e63']

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        this.calculateAttributes()
        let { p1x, p2x, p1y, p2y, centerX, centerY } = this.scaledCoordinates
        if (!p1x || !p2x || !p1y || !p2y || !centerX || !centerY) return

        let previousLevel: number[] = []
        if (this._settings.showTrendLine) {
            ctx.beginPath()
            ctx.moveTo(p1x, p1y)
            ctx.lineTo(p2x, p2y)
            ctx.globalAlpha = 0.5
            ctx.strokeStyle = this.sectionColors[0]
            ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio
            ctx.setLineDash([8, 4])
            ctx.stroke()
            ctx.closePath()

        }
        ctx.setLineDash([])
        this.sections.map((s, i) => {
            const scaledX = scaledCoordinate(s[0], this.horizontalPixelRatio)
            const scaledY = scaledCoordinate(this.priceToCoordinate(s[1]) || 0, this.verticalPixelRatio)
            ctx.beginPath()
            ctx.ellipse(centerX, centerY, scaledX, Math.abs(centerY - scaledY), 0, 0, 2 * Math.PI)
            const color = this.sectionColors[i]
            ctx.globalAlpha = 0.7
            ctx.strokeStyle = color
            ctx.stroke()
            if (previousLevel.length) {
                const scaledX = scaledCoordinate(previousLevel[0], this.horizontalPixelRatio)
                const scaledY = scaledCoordinate(this.priceToCoordinate(previousLevel[1]) || 0, this.verticalPixelRatio)
                ctx.ellipse(centerX, centerY, scaledX, Math.abs(centerY - scaledY), 0, 0, 2 * Math.PI)
                ctx.closePath()
            }

            ctx.globalAlpha = 0.15
            ctx.fillStyle = color
            ctx.fill('evenodd')
            previousLevel = s
        })

        this.sections.map((s, i) => this.renderSectionText(ctx, i))
    };

    renderSectionText(ctx: CanvasRenderingContext2D, index: number) {
        let { p1x, p2x, p1y, p2y, centerX } = this.scaledCoordinates
        if (!p1x || !p2x || !p1y || !p2y || !centerX) return
        const level = this.sectionLevels[index]
        const price = this.sections[index]
        const scaledY = scaledCoordinate(this.priceToCoordinate(price[1]) || 0, this.verticalPixelRatio)
        const text = `${level} (${price[1].toFixed(2)})`
        ctx.font = 12 * window.devicePixelRatio + 'px sans-serif'
        ctx.fillStyle = this.sectionColors[index]
        ctx.textBaseline = 'top'
        ctx.globalAlpha = 1
        const dim = ctx.measureText(text)
        const textWidth = dim.width
        let startX = centerX - textWidth - 5
        ctx.fillText(text, startX, scaledY + 2)
    }

    calculateSections() {
        const { p1, p2 } = this._state
        if (!p1 || !p2 || !this.center.y) return

        let min = p2
        if (p2.y > p1.y) min = p1

        const rangeY = this.center.y - min.y
        const rangeX = Math.abs(this.scaledCoordinates.centerX - this.scaledCoordinates.p1x)

        this.sections = this.sectionLevels.map((v) => [
            rangeX * v,
            this.center.y - rangeY * v
        ])

    }

    calculateAttributes() {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return
        this.center.y = (p1.y + p2.y) / 2
        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.scaledCoordinates.p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.centerX = (this.scaledCoordinates.p1x + this.scaledCoordinates.p2x) / 2
            this.scaledCoordinates.centerY = (this.scaledCoordinates.p1y + this.scaledCoordinates.p2y) / 2
        }
        this.calculateSections()
    }


    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {

        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            this._state.p2 = point
            !preview && this.onDrawingEnd()
        }
        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2 } = this._state

        p1 && handles.push(p1)
        p2 && handles.push(p2)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { centerX, centerY } = this.scaledCoordinates

        const length = this.sections.length
        if (length < 2) return null

        const primaryRadius = Math.abs(scaledCoordinate(this.sections[length - 1][0], this.horizontalPixelRatio))
        const secondaryRadius = Math.abs(scaledCoordinate(this.priceToCoordinate(this.sections[length - 1][1]) || 0, this.verticalPixelRatio) - centerY)

        const d = Math.pow(scaledX - centerX, 2) / Math.pow(primaryRadius, 2) + Math.pow(scaledY - centerY, 2) / Math.pow(secondaryRadius, 2)

        if (d <= 1) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }
        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        this._state[id as keyof typeof this._state]!.x = time
        this._state[id as keyof typeof this._state]!.y = price
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {

        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        const sorted = [p1, p2].sort((a, b) => a.y - b.y)
        return [{
            id: 'price',
            p1: sorted[0].y,
            p2: sorted[1].y,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]

    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        const sorted = [p1, p2].sort((a, b) => (a.x as number) - (b.x as number))
        return [{
            id: 'timer',
            p1: sorted[0].x,
            p2: sorted[1].x,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }


    getAvailableSettings = (): DrawingSettingGroup<FibonacciCirclesDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#6397d4',
                        opacity: 0.7
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                },
                {
                    name: 'showTrendLine',
                    label: 'Show Trend Line',
                    type: SettingType.BOOLEAN,
                    defaultValue: true,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): FibonacciCirclesDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof FibonacciCirclesDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = ['labelColor', 'lineWidth']
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'fibonacciCircles'
    }
}

export default FibonacciCirclesDrawingTool